import type { CustomerStatus, Role } from "./types";

export type Tone = "blue" | "teal" | "amber" | "coral" | "neutral";

export const roleNames: Record<Role, string> = {
  SYSTEM_ADMIN: "시스템 관리자",
  OPERATOR: "운영",
  BILLING_STAFF: "결제 담당",
  STAFF_VIEWER: "열람",
  CUSTOMER_OWNER: "소유자",
  CUSTOMER_ADMIN: "관리자",
  CUSTOMER_MEMBER: "멤버",
  CUSTOMER_VIEWER: "열람",
};

/** 화면에 보이는 내 역할 (묶인 역할 이름들). */
export function rolesLabel(me: { kind: string; roles?: { name: string }[] }): string {
  if (me.kind === "machine") return "머신 전용";
  return (me.roles ?? []).map((r) => r.name).join(" · ") || "역할 없음";
}

export const serviceNames: Record<CustomerStatus, string> = {
  active: "정상 이용",
  overdue: "결제 유예",
  suspended: "서비스 정지",
  terminated: "서비스 해지",
};
export const serviceTone: Record<CustomerStatus, Tone> = { active: "teal", overdue: "amber", suspended: "coral", terminated: "coral" };

export const sanctionNames = ["없음", "새 작업 중지", "실행 중 작업 취소", "프로그램 잠금"];

/** Run 상태 (job_run). */
const runStatus: Record<string, [string, Tone]> = {
  queued: ["대기", "blue"],
  held: ["보류", "amber"],
  waiting_machine: ["머신 대기", "amber"],
  running: ["진행 중", "blue"],
  succeeded: ["성공", "teal"],
  failed: ["실패", "coral"],
  dead: ["최종 실패", "coral"],
  cancelled: ["취소", "neutral"],
};
/** Task 상태. */
const taskStatus: Record<string, [string, Tone]> = {
  queued: ["대기", "blue"],
  assigned: ["배정됨", "blue"],
  running: ["진행 중", "blue"],
  awaiting_approval: ["승인 대기", "amber"],
  succeeded: ["성공", "teal"],
  failed: ["실패", "coral"],
  cancelled: ["취소", "neutral"],
};
export const runLabel = (s: string): [string, Tone] => runStatus[s] ?? [s, "neutral"];

/** 게시 단계 이름 (브릿지 5단계). */
export const stageNames: Record<string, string> = { review: "원고 검토", run: "작업 수행", posted: "작업 완료", verify: "완료 검토", done: "최종 보고" };

/**
 * 고객용 발행 상태 (2026-10-16 고객 발행). Run·Task 상태를 고객 화면 명칭으로 바꾼다.
 * 확인 중 · 수정 요청 · 발행 대기 · 발행 중(단계) · 발행됨 · 발행 실패 · 취소됨 · 반려됨 · 삭제됨.
 */
export function publishLabel(r: { status: string; errorKind?: string | null; taskStatus?: string | null; stage?: string | null; reviewStatus?: string | null; jobRunAt?: string | null; scheduledAt?: string | null }): [string, Tone] {
  if (r.reviewStatus === "deleted") return ["삭제됨", "neutral"];
  if (r.status === "held") {
    if (r.errorKind === "approval_required") return ["확인 중", "amber"];
    if (r.errorKind === "changes_requested") return ["수정 요청", "coral"];
    return ["발행 대기", "blue"];
  }
  if (r.status === "queued" || r.status === "waiting_machine") {
    if (r.taskStatus === "running" || r.taskStatus === "assigned") return [r.stage ? `발행 중 · ${stageNames[r.stage] ?? r.stage}` : "발행 중", "blue"];
    return ["발행 대기", "blue"];
  }
  if (r.status === "running") return [r.stage ? `발행 중 · ${stageNames[r.stage] ?? r.stage}` : "발행 중", "blue"];
  if (r.status === "succeeded") return ["발행됨", "teal"];
  if (r.status === "failed" || r.status === "dead") return ["발행 실패", "coral"];
  if (r.status === "cancelled") return r.errorKind === "rejected" ? ["반려됨", "coral"] : ["취소됨", "neutral"];
  return runLabel(r.status);
}
export const taskLabel = (s: string): [string, Tone] => taskStatus[s] ?? [s, "neutral"];

export const taskTypeNames: Record<string, string> = {
  text_task: "텍스트 작업",
  naver_blog_post: "네이버 블로그 발행",
  phone_task: "폰 작업",
  browser_task: "브라우저 작업",
  naver_blog_delete: "네이버 블로그 글 삭제",
};
export const runKindNames: Record<string, string> = { machine_task: "디바이스 작업", system: "시스템 작업" };

export const errorKindNames: Record<string, string> = {
  device_unavailable: "폰 연결 문제",
  pre_step_failed: "사전 단계 실패",
  login_required: "로그인 필요",
  llm_limit: "AI 사용 한도",
  timeout: "시간 초과",
  max_steps: "단계 한도 초과",
  agent_failed: "작업 실패",
  system: "시스템 오류",
  worker_lost: "머신 연결 끊김",
  invalid_input: "입력 오류",
  customer_inactive: "고객사 상태",
  quota_exceeded: "월 한도 초과",
  cancelled: "취소",
  machine_unavailable: "머신 없음",
  worker_rejected: "머신이 거절",
  task_failed: "작업 실패",
  review_rejected: "원고 검토 반려",
  needs_human: "사람 확인 필요",
  unknown_result: "결과 불명 (게시 후 끊김)",
  verify_failed: "완료 검토 불일치",
  expired: "기한 만료",
  approval_required: "운영팀 확인 대기",
  changes_requested: "원고 수정 요청",
  scheduled: "승인 · 예약 대기",
  rejected: "반려",
};

export const machineStatus = (m: { status: string; connected: boolean; pairedAt: string | null; deleted?: boolean }): [string, Tone] => {
  if (m.deleted) return ["삭제됨", "neutral"];
  if (m.status === "retired") return ["사용 종료", "neutral"];
  if (m.status === "revoked") return ["페어링 해제", "neutral"];
  if (!m.pairedAt || m.status === "unpaired") return ["미연결", "neutral"];
  if (m.status === "paused") return ["일시 중지", "amber"];
  return m.connected ? ["온라인", "teal"] : ["연결 끊김", "coral"];
};
export const deviceState: Record<string, [string, Tone]> = {
  device: ["연결됨", "teal"],
  unauthorized: ["승인 대기", "amber"],
  offline: ["오프라인", "coral"],
  unknown: ["확인 필요", "amber"],
};

const pad = (n: number) => String(n).padStart(2, "0");
export function fmtTime(s: string | null | undefined): string {
  if (!s) return "-";
  const d = new Date(s);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return sameDay ? hm : `${d.getMonth() + 1}.${pad(d.getDate())} ${hm}`;
}
export function fmtDateTime(s: string | null | undefined): string {
  if (!s) return "-";
  const d = new Date(s);
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
export function fmtDate(s: string | null | undefined): string {
  if (!s) return "-";
  const d = new Date(s);
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}
export function ago(s: string | null | undefined): string {
  if (!s) return "기록 없음";
  const sec = Math.round((Date.now() - new Date(s).getTime()) / 1000);
  if (sec < 10) return "방금";
  if (sec < 60) return `${sec}초 전`;
  if (sec < 3600) return `${Math.floor(sec / 60)}분 전`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}시간 전`;
  return `${Math.floor(sec / 86400)}일 전`;
}
export function duration(from: string | null, to: string | null): string {
  if (!from) return "-";
  const ms = (to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime();
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s}초` : `${Math.floor(s / 60)}분 ${pad(s % 60)}초`;
}
export const num = (n: number | null | undefined) => (n === null || n === undefined ? "-" : n.toLocaleString("ko-KR"));
export function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString("ko-KR");
}
export const initials = (name: string) => name.trim().slice(0, 1) || "?";
